/**
 * Custom Infinite Scroll (Scrollify-like)
 * - Mode 1: auto load on scroll
 * - Mode 2: load more button
 * - Uses #gscollectionbottom / #gsloadmore / #gsloaderimg like the pasted script
 * - Persists loaded HTML in sessionStorage and restores when returning from a product page
 * - Preserves existing query params (sort_by, view, q, constraint, etc.) while changing page=
 * - Adds <meta name="robots" content="noindex"> when ?page= is present (like your paste)
 *
 * Setup:
 * 1) Add this file to assets and include on collection/search templates.
 * 2) (Recommended) Set selectors via window.InfiniteScrollConfig (see bottom).
 */
(function () {
  'use strict';

  const STORAGE = {
    collectionUrl: 'gscollectionurl',
    collectionContent: 'gscollectioncontent',
    paginateContent: 'gspaginatecontent',
    pageNr: 'gscollectionpagenr',
    productViewed: 'gsproductviewed'
  };

  function safeGetSession(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSetSession(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) {}
  }

  function stripPageParam(urlString) {
    try {
      const u = new URL(urlString);
      u.searchParams.delete('page');
      // Normalize trailing ? if empty
      u.search = u.searchParams.toString() ? `?${u.searchParams.toString()}` : '';
      return u.toString();
    } catch (e) {
      return urlString;
    }
  }

  function currentIsProductPage() {
    return window.location.pathname.indexOf('/products/') !== -1;
  }

  function currentIsCollectionOrSearch() {
    const p = window.location.pathname;
    return p.indexOf('/collections/') !== -1 || p.indexOf('/search') !== -1;
  }

  function addNoIndexOnPaginated() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('page')) return;
    if (document.querySelector('meta[name="robots"][content="noindex"]')) return;

    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
  }

  // Mark that user visited a product page (used for restore behavior)
  if (currentIsProductPage()) {
    safeSetSession(STORAGE.productViewed, '1');
    return;
  }

  addNoIndexOnPaginated();

  class InfiniteScroll {
    constructor(config) {
      this.config = config;

      this.loading = false;
      this.page = this.getCurrentPageFromUrl();
      this.baseUrlNoPage = stripPageParam(window.location.href);

      this.productsSelector = config.productsSelector || null;
      this.paginationSelector = config.paginationSelector || null;

      this.productsEl = null;
      this.paginationEl = null;
      this.bottomEl = null;
      this.buttonEl = null;
      this.loaderImgEl = null;

      this.observer = null;
      this.boundScroll = null;

      this.init();
    }

    getCurrentPageFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const p = parseInt(params.get('page') || '1', 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    }

    // Auto-detect common Shopify containers if selectors not provided
    detectProductsContainer() {
      const candidates = [
        this.config.productsSelector,
        '#bc-sf-filter-products',
        '.product-grid',
        '.ProductList',
        '.collection-grid',
        '.products',
        '.product-loop',
        '.collection__products',
        '[data-section-type="collection-template"] .grid',
        'main .collection-grid__wrapper .grid'
      ].filter(Boolean);

      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (el && (el.querySelector('a[href*="/products/"]') || el.querySelector('[class*="product"]'))) {
          this.productsSelector = sel;
          return el;
        }
      }
      return null;
    }

    detectPagination() {
      const candidates = [
        this.config.paginationSelector,
        '.shopify-pagination',
        '.pagination',
        '.pagination-wrapper',
        '[class*="pagination"]'
      ].filter(Boolean);

      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (el && el.querySelector('a[href*="page="]')) {
          this.paginationSelector = sel;
          return el;
        }
      }
      return null;
    }

    init() {
      if (!currentIsCollectionOrSearch()) return;

      this.productsEl = this.detectProductsContainer();
      this.paginationEl = this.detectPagination();

      if (!this.productsEl || !this.paginationEl) return;

      // Restore from sessionStorage if returning from PDP
      this.tryRestore();

      // If already ended previously, stop
      const storedPageNr = parseInt(safeGetSession(STORAGE.pageNr) || '', 10);
      if (storedPageNr === -1) {
        this.removeBottomUI();
        return;
      }

      this.createBottomUI();
      this.hidePagination();

      if (this.config.mode === 1) {
        this.attachAutoLoad();
      } else {
        this.attachButtonLoad();
      }
    }

    tryRestore() {
      const productViewed = safeGetSession(STORAGE.productViewed);
      const storedUrl = safeGetSession(STORAGE.collectionUrl);
      const storedContent = safeGetSession(STORAGE.collectionContent);
      const storedPaginate = safeGetSession(STORAGE.paginateContent);
      const storedPageNr = safeGetSession(STORAGE.pageNr);

      const canonicalCurrent = stripPageParam(window.location.href);
      const canonicalStored = storedUrl ? stripPageParam(storedUrl) : null;

      // Only restore if user came back from a product page and URL matches
      if (productViewed !== '1') return;
      if (!canonicalStored || canonicalStored !== canonicalCurrent) return;
      if (!storedContent || !storedPaginate) return;

      // Replace current content with stored loaded content
      this.productsEl.innerHTML = storedContent;
      this.paginationEl.innerHTML = storedPaginate;

      const pn = parseInt(storedPageNr || '1', 10);
      if (Number.isFinite(pn) && pn > 0) this.page = pn;

      safeSetSession(STORAGE.productViewed, '0');

      // Notify any theme code
      this.dispatchEvent('infinite-scroll:restored', { page: this.page });
      this.reInitIntegrations();
    }

    createBottomUI() {
      this.removeBottomUI();

      const bottom = document.createElement('div');
      bottom.id = 'gscollectionbottom';
      bottom.style.cssText = 'width:100%;text-align:center;margin:20px 0;clear:both;';

      if (this.config.mode === 2) {
        const btn = document.createElement('button');
        btn.id = 'gsloadmore';
        btn.type = 'button';
        btn.textContent = this.config.buttonText;
        btn.style.cssText = 'max-width:250px;min-height:35px;cursor:pointer;padding:10px 20px;border:1px solid #ccc;border-radius:4px;background:#fff;font-size:16px;';

        const img = document.createElement('img');
        img.id = 'gsloaderimg';
        img.src = this.config.loaderGif;
        img.alt = 'Loading';
        img.style.cssText = 'margin:10px auto 0px;display:none;max-width:200px;';

        bottom.appendChild(btn);
        bottom.appendChild(img);

        this.buttonEl = btn;
        this.loaderImgEl = img;
      } else {
        const img = document.createElement('img');
        img.id = 'gsloaderimg';
        img.src = this.config.loaderGif;
        img.alt = 'Loading';
        img.style.cssText = 'margin-top:10px;display:block;max-width:200px;';
        bottom.appendChild(img);

        this.loaderImgEl = img;
      }

      this.paginationEl.insertAdjacentElement('afterend', bottom);
      this.bottomEl = bottom;
    }

    removeBottomUI() {
      const old = document.getElementById('gscollectionbottom');
      if (old) old.remove();
      this.bottomEl = null;
      this.buttonEl = null;
      this.loaderImgEl = null;
    }

    hidePagination() {
      if (!this.paginationEl) return;
      this.paginationEl.style.display = 'none';
    }

    attachAutoLoad() {
      // Prefer IntersectionObserver (more stable than scroll math)
      if ('IntersectionObserver' in window && this.bottomEl) {
        this.observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.loadNextPage();
          }
        }, { root: null, rootMargin: '200px 0px', threshold: 0.01 });

        this.observer.observe(this.bottomEl);
        return;
      }

      // Fallback: scroll listener
      this.boundScroll = () => {
        if (!this.bottomEl) return;
        const rect = this.bottomEl.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 200 && rect.bottom >= 0) {
          this.loadNextPage();
        }
      };
      document.addEventListener('scroll', this.boundScroll, { passive: true });
      window.addEventListener('resize', this.boundScroll);
      window.addEventListener('orientationchange', this.boundScroll);
    }

    attachButtonLoad() {
      if (!this.buttonEl) return;
      this.buttonEl.addEventListener('click', () => this.loadNextPage());
    }

    buildUrlForPage(pageNumber) {
      const u = new URL(this.baseUrlNoPage);
      const params = u.searchParams;

      // Preserve existing params; only set page=
      params.set('page', String(pageNumber));
      u.search = params.toString() ? `?${params.toString()}` : '';
      return u.toString();
    }

    docHasNextPage(doc, nextPageNumber) {
      // Common patterns: rel="next" or link containing page=nextPageNumber
      if (doc.querySelector('a[rel="next"]')) return true;

      // Try to find any link to the next page
      const needle1 = `page=${nextPageNumber}`;
      const links = Array.from(doc.querySelectorAll('a[href]'));
      return links.some(a => (a.getAttribute('href') || '').indexOf(needle1) !== -1);
    }

    async loadNextPage() {
      if (this.loading) return;
      if (!this.productsEl || !this.paginationEl) return;

      this.loading = true;

      if (this.config.mode === 2 && this.buttonEl) {
        this.buttonEl.disabled = true;
        this.buttonEl.textContent = 'Loading...';
      }
      if (this.loaderImgEl && this.config.mode === 2) {
        this.loaderImgEl.style.display = 'block';
      }

      const nextPage = this.page + 1;
      const nextUrl = this.buildUrlForPage(nextPage);

      try {
        const res = await fetch(nextUrl, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const nextProductsEl = this.productsSelector ? doc.querySelector(this.productsSelector) : null;
        const nextPaginationEl = this.paginationSelector ? doc.querySelector(this.paginationSelector) : null;

        if (!nextProductsEl || !nextProductsEl.innerHTML.trim()) {
          this.stopNoMorePages();
          return;
        }

        // Append products
        const fragment = document.createElement('div');
        fragment.innerHTML = nextProductsEl.innerHTML;

        // Remove embedded scripts/styles in appended chunk (prevents duplication)
        fragment.querySelectorAll('script, style').forEach(n => n.remove());

        this.productsEl.insertAdjacentHTML('beforeend', fragment.innerHTML);

        // Update pagination markup from next page (even though it stays hidden)
        if (nextPaginationEl) {
          this.paginationEl.innerHTML = nextPaginationEl.innerHTML;
        }

        // Update current page
        this.page = nextPage;

        // Persist state like the pasted script does
        this.persistState();

        // Re-init integrations/widgets similar to the pasted script’s post-append calls
        this.reInitIntegrations();

        this.dispatchEvent('infinite-scroll:appended', { page: this.page, url: nextUrl });

        // Stop if no next page exists after this one
        const hasNext = this.docHasNextPage(doc, this.page + 1);
        if (!hasNext) {
          this.stopNoMorePages();
        }
      } catch (e) {
        // On error, stop to avoid loops
        this.stopNoMorePages();
        console.error('[InfiniteScroll] Failed:', e);
      } finally {
        this.loading = false;

        if (this.config.mode === 2 && this.buttonEl) {
          this.buttonEl.disabled = false;
          this.buttonEl.textContent = this.config.buttonText;
        }
        if (this.loaderImgEl && this.config.mode === 2) {
          this.loaderImgEl.style.display = 'none';
        }
      }
    }

    persistState() {
      // Store canonical URL (without page=) + current loaded markup
      safeSetSession(STORAGE.collectionUrl, this.baseUrlNoPage);
      safeSetSession(STORAGE.collectionContent, this.productsEl.innerHTML);
      safeSetSession(STORAGE.paginateContent, this.paginationEl.innerHTML);
      safeSetSession(STORAGE.pageNr, String(this.page));
      safeSetSession(STORAGE.productViewed, '0');
    }

    stopNoMorePages() {
      // Match pasted script behavior: gscollectionpagenr = -1 and remove bottom UI
      safeSetSession(STORAGE.pageNr, '-1');

      if (this.observer) {
        try { this.observer.disconnect(); } catch (e) {}
        this.observer = null;
      }
      if (this.boundScroll) {
        document.removeEventListener('scroll', this.boundScroll);
        window.removeEventListener('resize', this.boundScroll);
        window.removeEventListener('orientationchange', this.boundScroll);
        this.boundScroll = null;
      }
      this.removeBottomUI();
      this.dispatchEvent('infinite-scroll:ended', { page: this.page });
    }

    dispatchEvent(name, detail) {
      try {
        document.dispatchEvent(new CustomEvent(name, { detail }));
      } catch (e) {}
    }

    reInitIntegrations() {
      // Shopify Product Reviews (SPR)
      try {
        if (window.SPR && typeof window.SPR.initRatingHandler === 'function') window.SPR.initRatingHandler();
        if (window.SPR && typeof window.SPR.initDomEls === 'function') window.SPR.initDomEls();
        if (window.SPR && typeof window.SPR.loadBadges === 'function') window.SPR.loadBadges();
      } catch (e) {}

      // Yotpo (safe)
      try {
        if (window.yotpo && window.Yotpo && window.Yotpo.API) {
          const api = new window.Yotpo.API(window.yotpo);
          if (api && typeof api.refreshWidgets === 'function') api.refreshWidgets();
        }
      } catch (e) {}

      // Currency converters (Doubly)
      try {
        if (window.DoublyGlobalCurrency && typeof window.DoublyGlobalCurrency.convertAll === 'function') {
          const el = document.querySelector('[name="doubly-currencies"]');
          window.DoublyGlobalCurrency.convertAll(el ? el.value : undefined);
          setTimeout(() => {
            try { window.DoublyGlobalCurrency.convertAll(el ? el.value : undefined); } catch (e) {}
          }, 1000);
        }
      } catch (e) {}

      // Wishlist reload (Smart Wishlist pattern)
      try {
        if (typeof window.ReloadSmartWishlist !== 'undefined' && typeof window.ReloadSmartWishlist === 'function') {
          window.ReloadSmartWishlist();
        }
      } catch (e) {}

      // Theme cart-drawer quick add (if your theme exposes it)
      try {
        if (window.HsCartDrawer && typeof window.HsCartDrawer.quickBuyByttonByCollections === 'function') {
          window.HsCartDrawer.quickBuyByttonByCollections(window.HsCartDrawer.json, window.HsCartDrawer.query);
        }
      } catch (e) {}

      // Generic: kick lazyload libraries
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
    }
  }

  // ---- Boot ----
  // Optional config:
  // window.InfiniteScrollConfig = {
  //   mode: 1, // 1=auto, 2=button
  //   loaderGif: 'https://media.tenor.com/uuEPRFM5xdcAAAAi/loader.gif',
  //   buttonText: 'Load more',
  //   productsSelector: '.product-grid',   // IMPORTANT: set these for your theme
  //   paginationSelector: '.pagination'
  // };

  const cfg = Object.assign(
    {
      mode: 1,
      //loaderGif: 'https://media.tenor.com/uuEPRFM5xdcAAAAi/loader.gif',
      loaderGif: 'https://cdn.shopify.com/s/files/1/0644/2457/5191/files/loader.gif?v=1770197851',
      buttonText: 'Load more',
      productsSelector: null,
      paginationSelector: null
    },
    window.InfiniteScrollConfig || {}
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new InfiniteScroll(cfg));
  } else {
    new InfiniteScroll(cfg);
  }
})();
